import { useEffect, useMemo } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { OverviewAccordionContextProvider, OverviewAccordionRoot } from '../../context/OverviewContextProvider'
import useOverviewContext from '../../context/OverviewContextProvider'
import useQueryContext from '../../context/QueryContext'
import useIsReadOnly from '../../hooks/useIsReadOnly'
import { useGetRoutines } from '../../services/api/routines.hooks'
import { Border, Colors, Spacing } from '../../styles'
import { icons } from '../../styles/images'
import { notifyReadOnlyRejection } from '../../utils/readOnly'
import { findRoutineByRouteId } from '../../utils/routines'
import { TOverviewView, TRoutine } from '../../utils/types'
import ActionsContainer from '../atoms/ActionsContainer'
import Flex from '../atoms/Flex'
import Spinner from '../atoms/Spinner'
import GTButton from '../atoms/buttons/GTButton'
import { BodyMedium } from '../atoms/typography/Typography'
import { Header } from '../molecules/Header'
import AccordionItem from '../overview/AccordionItem'
import RoutineEditModal from '../routines/RoutineEditModal'
import ScrollableListTemplate from '../templates/ScrollableListTemplate'

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${Spacing._12};
    padding: ${Spacing._24};
    color: ${Colors.text.muted};
    background: ${Colors.background.panel};
    border: ${Border.stroke.medium} dashed ${Colors.background.border};
    border-radius: ${Border.radius.medium};
`

const routineToView = (routine: TRoutine): TOverviewView => ({
    id: routine.id,
    name: routine.name,
    type: 'routine',
    routine_id: routine.id,
    is_reorderable: false,
    logo: 'generaltask',
    view_items: routine.items,
    view_item_ids: routine.items.map((item) => item.id),
    sources: [],
    is_linked: true,
    has_tasks_completed_today: routine.status === 'complete',
    status: routine.status,
    remaining_count: routine.remaining_count,
    streak: routine.streak,
})

interface RoutinesContentProps {
    routines: TRoutine[]
    views: TOverviewView[]
}

const RoutinesContent = ({ routines, views }: RoutinesContentProps) => {
    const navigate = useNavigate()
    const location = useLocation()
    const { routineId } = useParams()
    const { getIdFromOptimisticId } = useQueryContext()
    const { expandAll, collapseAll } = useOverviewContext()
    const resolvedRoutineId = routineId ? getIdFromOptimisticId(routineId) : undefined
    const selectedRoutine = findRoutineByRouteId(routines, routineId, resolvedRoutineId)
    const routeState = location.state as { routineModalKey?: string } | null
    const routineModalKey = routeState?.routineModalKey ?? routineId ?? 'closed'
    const isReadOnly = useIsReadOnly()
    const createRoutine = () => (isReadOnly ? notifyReadOnlyRejection() : navigate('/routines/new'))

    useEffect(() => {
        if (!routineId || !resolvedRoutineId || routineId === resolvedRoutineId) return
        navigate(`/routines/${resolvedRoutineId}`, {
            replace: true,
            state: { routineModalKey },
        })
    }, [navigate, resolvedRoutineId, routineId, routineModalKey])

    return (
        <>
            <Flex>
                <ScrollableListTemplate>
                    <Header folderName="Routines" />
                    <ActionsContainer
                        leftActions={
                            <GTButton
                                styleType="primary"
                                value="New routine"
                                icon={icons.plus}
                                onClick={createRoutine}
                            />
                        }
                        rightActions={
                            <>
                                <GTButton
                                    styleType="control"
                                    value="Collapse all"
                                    icon={icons.squareMinus}
                                    onClick={collapseAll}
                                />
                                <GTButton
                                    styleType="control"
                                    value="Expand all"
                                    icon={icons.squarePlus}
                                    onClick={expandAll}
                                />
                            </>
                        }
                    />
                    <OverviewAccordionRoot>
                        {views.map((view) => (
                            <AccordionItem key={view.id} list={view} />
                        ))}
                    </OverviewAccordionRoot>
                    {views.length === 0 && (
                        <EmptyState>
                            <BodyMedium>No routines yet</BodyMedium>
                            <GTButton
                                styleType="primary"
                                value="Create routine"
                                icon={icons.plus}
                                onClick={createRoutine}
                            />
                        </EmptyState>
                    )}
                </ScrollableListTemplate>
            </Flex>
            <RoutineEditModal
                key={routineModalKey}
                routine={selectedRoutine}
                isCreating={routineId === 'new'}
            />
        </>
    )
}

const RoutinesView = () => {
    const { data: routines, isLoading } = useGetRoutines()
    const views = useMemo(() => routines?.map(routineToView) ?? [], [routines])
    if (isLoading || !routines) return <Spinner />
    const firstUnfinished = routines.find((routine) => routine.status === 'current')
    return (
        <OverviewAccordionContextProvider listIds={views.map((view) => view.id)} initialOpenId={firstUnfinished?.id}>
            <RoutinesContent routines={routines} views={views} />
        </OverviewAccordionContextProvider>
    )
}

export default RoutinesView
